import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import Swal from 'sweetalert2';

import { SousCategorie } from 'app/models/sous-categorie';
import { SousCategorieService } from 'app/services/sous-categorie/sous-categorie.service';
import { AwscontrolService } from 'app/services/aws/awscontrol.service';

@Component({
  selector: 'app-modifier-sous-categorie',
  templateUrl: './modifier-sous-categorie.component.html',
  styleUrls: ['./modifier-sous-categorie.component.scss']
})
export class ModifierSousCategorieComponent implements OnInit {
  private readonly s3BaseUrl = 'https://tunisie-immob.s3.eu-west-3.amazonaws.com/';

  sousCategorie: SousCategorie = new SousCategorie();
  idSouCat: string | null = null;
  produitId: string | null = null; // <-- store product ID for navigation

  // image_url: only one image (as a key string)
  mainImageKey: string = ''; // store the S3 key (without domain)
  uploadingMain = false;

  // listimages: array of full URLs or keys
  secondaryImages: string[] = [];
  uploadingSecondary = false;

  isSubmitting = false;

  constructor(
    private sousCatService: SousCategorieService,
    private awsService: AwscontrolService,
    private activatedRoute: ActivatedRoute,
    private router: Router, // <-- added Router
    private location: Location
  ) {}

  ngOnInit(): void {
    this.idSouCat = this.activatedRoute.snapshot.paramMap.get('id');
    this.getSousCategorieDetails();
  }

  getSousCategorieDetails(): void {
    if (!this.idSouCat) return;
    this.sousCatService.getSousCategorie(this.idSouCat).subscribe({
      next: (data: any) => {
        this.sousCategorie = { ...data } as SousCategorie;
        // Store product ID for navigation
        this.produitId = data.produit_id || data.produitId || null;

        // Load main image (image_url) – take the first one, store as key
        if (data.image_url) {
          const urls = data.image_url.split(',').map((u: string) => u.trim()).filter((u: string) => u);
          if (urls.length > 0) {
            // Store as key (remove domain if present)
            const first = urls[0];
            this.mainImageKey = first.startsWith('http') ? first.replace(this.s3BaseUrl, '') : first;
          } else {
            this.mainImageKey = '';
          }
        } else {
          this.mainImageKey = '';
        }

        // Load secondary images (listimages) – array of full URLs or keys
        this.secondaryImages = (data.listimages && Array.isArray(data.listimages)) ? [...data.listimages] : [];
      },
      error: () => {
        Swal.fire({ icon: 'error', title: 'Erreur', text: 'Impossible de charger les détails', confirmButtonColor: '#f44336' });
      }
    });
  }

  // Get full URL for display
  getFullImageUrl(urlOrKey: string): string {
    if (!urlOrKey) return 'assets/default-subproduct.png';
    if (urlOrKey.startsWith('http')) return urlOrKey;
    return this.s3BaseUrl + urlOrKey;
  }

  // ---------- Upload for MAIN image (single) ----------
  onMainFileSelected(event: any): void {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      Swal.fire({ icon: 'warning', title: 'Format invalide', text: 'Veuillez sélectionner une image', timer: 2000 });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      Swal.fire({ icon: 'warning', title: 'Fichier trop volumineux', text: 'Taille max 5MB', timer: 2000 });
      return;
    }

    this.uploadingMain = true;
    this.awsService.uploadFile(file, 'sous-categories').subscribe({
      next: (response) => {
        this.uploadingMain = false;
        if (response.success && response.urls && response.urls.length) {
          const fullUrl = response.urls[0];
          // Store as key (remove domain)
          const key = fullUrl.replace(this.s3BaseUrl, '');
          this.mainImageKey = key;
          Swal.fire({ icon: 'success', title: 'Image mise à jour', timer: 1500, showConfirmButton: false });
        } else {
          Swal.fire({ icon: 'error', title: 'Erreur', text: response.error || 'Upload failed' });
        }
        event.target.value = '';
      },
      error: (err) => {
        this.uploadingMain = false;
        console.error(err);
        Swal.fire({ icon: 'error', title: 'Erreur', text: 'Impossible de télécharger l\'image' });
        event.target.value = '';
      }
    });
  }

  // ---------- Upload for SECONDARY images (multiple) ----------
  onSecondaryFilesSelected(event: any): void {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    this.uploadingSecondary = true;

    const validFiles: File[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.type.startsWith('image/') && file.size <= 5 * 1024 * 1024) {
        validFiles.push(file);
      } else {
        Swal.fire({ icon: 'warning', title: 'Fichier ignoré', text: `${file.name} invalide (max 5MB, format image)`, timer: 2000 });
      }
    }

    if (!validFiles.length) {
      this.uploadingSecondary = false;
      return;
    }

    this.awsService.uploadFiles(validFiles, 'sous-categories').subscribe({
      next: (response) => {
        this.uploadingSecondary = false;
        if (response.success && response.urls && response.urls.length) {
          // Store full URLs (or keys, but we keep full URLs)
          this.secondaryImages.push(...response.urls);
          Swal.fire({ icon: 'success', title: `${response.urls.length} image(s) ajoutée(s)`, timer: 1500, showConfirmButton: false });
        } else {
          Swal.fire({ icon: 'error', title: 'Erreur', text: response.error || 'Upload failed' });
        }
        event.target.value = '';
      },
      error: (err) => {
        this.uploadingSecondary = false;
        console.error(err);
        Swal.fire({ icon: 'error', title: 'Erreur', text: 'Impossible de télécharger les images' });
        event.target.value = '';
      }
    });
  }

  // Remove main image (just clears the key)
  removeMainImage(): void {
    Swal.fire({
      title: 'Retirer l\'image ?',
      text: 'Cette image sera retirée de la liste (non supprimée du cloud).',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      confirmButtonText: 'Oui, retirer'
    }).then((result) => {
      if (result.isConfirmed) {
        this.mainImageKey = '';
        Swal.fire({ icon: 'success', title: 'Retirée', timer: 1500, showConfirmButton: false });
      }
    });
  }

  // Remove secondary image
  removeSecondaryImage(index: number): void {
    Swal.fire({
      title: 'Retirer l\'image ?',
      text: 'Cette image sera retirée de la liste (non supprimée du cloud).',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      confirmButtonText: 'Oui, retirer'
    }).then((result) => {
      if (result.isConfirmed) {
        this.secondaryImages.splice(index, 1);
        Swal.fire({ icon: 'success', title: 'Retirée', timer: 1500, showConfirmButton: false });
      }
    });
  }

  // Build payload (no website)
  private buildPayload(): any {
    return {
      name: this.sousCategorie.name?.trim() || '',
      description: this.sousCategorie.description?.trim() || '',
      category: this.sousCategorie.category?.trim() || '',
      prix: this.sousCategorie.prix?.trim() || '',
      image_url: this.mainImageKey || '', // single key
      listimages: [...this.secondaryImages] // array of URLs
    };
  }

  // Submit update
  updateSousCat(): void {
    // No validation – all fields optional
    this.isSubmitting = true;
    const payload = this.buildPayload();
    console.log('Payload envoyé:', payload);

    Swal.fire({ title: 'Mise à jour...', text: 'Veuillez patienter', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

    if (!this.idSouCat) {
      Swal.close();
      Swal.fire({ icon: 'error', title: 'ID manquant', text: 'Impossible de mettre à jour', confirmButtonColor: '#f44336' });
      this.isSubmitting = false;
      return;
    }

    this.sousCatService.updateSousCategorie(this.idSouCat, payload).subscribe({
      next: () => {
        this.isSubmitting = false;
        Swal.close();
        Swal.fire({ icon: 'success', title: 'Succès !', text: 'Sous-catégorie mise à jour', timer: 1500, showConfirmButton: false })
          .then(() => this.goToList());
      },
      error: (error: HttpErrorResponse) => {
        this.isSubmitting = false;
        Swal.close();
        let errorMessage = 'Erreur lors de la mise à jour';
        if (error.error?.message) errorMessage = error.error.message;
        Swal.fire({ icon: 'error', title: 'Erreur', text: errorMessage, confirmButtonColor: '#f44336' });
      }
    });
  }

  // Cancel: redirect to list
  cancel(): void {
    this.goToList();
  }

  // Navigate to list-sous-produits
  private goToList(): void {
    if (this.produitId) {
      this.router.navigate(['/list-sous-produits', this.produitId]);
    } else {
      this.location.back();
    }
  }

  navigateBack(): void {
    this.cancel();
  }

  resetForm(): void {
    Swal.fire({
      title: 'Réinitialiser ?',
      text: 'Toutes les modifications non sauvegardées seront perdues',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      confirmButtonText: 'Oui',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.getSousCategorieDetails();
        Swal.fire({ icon: 'success', title: 'Réinitialisé', timer: 1500, showConfirmButton: false });
      }
    });
  }
}